import { useEffect, type ReactNode } from 'react';
import { useAnchorCell } from './anchorCell';
import { useInIndexTable } from './indexTableSurface';
import { describeOverflow } from './overflow';
import { Tooltip } from '../Tooltip';

// One message per session, not one per row — 30 identical errors help nobody.
let warnedAboutSecondary = false;

/**
 * Two-line cell: a darker primary over a muted secondary — reference §14.
 *
 * Emphasis is by **colour only, never bold**. Weight is reserved for the header band, so a
 * bold cell would read as a different kind of element. In the anchor column the primary
 * darkens another step and carries a medium weight, because there it is the row's click
 * target.
 */
export function StackedCell({
  primary,
  secondary,
  mono = false,
  primaryClassName = '',
  secondaryClassName,
  approvedDeviation = false,
}: {
  primary: ReactNode;
  /** Renders only when non-empty — an empty second line leaves a ragged row. */
  secondary?: ReactNode;
  mono?: boolean;
  /** Appended to the primary line, for layout such as `block truncate`. */
  primaryClassName?: string;
  /** Full override of the secondary line's classes — must include `font-mono` itself. */
  secondaryClassName?: string;
  /**
   * Silences the index-table refusal for ONE stacked anchor CLAUDE.md lists as an approved
   * deviation (the Plantilla post: title over its code, 19 Oct 2026). Not a convenience —
   * `test:conventions` allows it in the listed files only.
   */
  approvedDeviation?: boolean;
}) {
  const anchor = useAnchorCell();
  const inIndexTable = useInIndexTable();
  /*
    The overflow rule only applies to text. A caller may pass any node — a badge, a link —
    and there is nothing to count words in, so those keep the old ellipsis.
  */
  const primaryOverflow =
    typeof primary === 'string' ? describeOverflow(primary) : { className: 'truncate' as const };

  const secondaryOverflow =
    typeof secondary === 'string' ? describeOverflow(secondary) : { className: 'truncate' as const };

  const hasSecondary = secondary !== null && secondary !== undefined && secondary !== '';

  /*
    A standard index table gives every value its own column, so a second line here is a
    layout the shell does not accept. Caught in development rather than typed away: the
    same component is legitimately stacked in preview tables, modals, and cards, so the
    constraint is about WHERE it renders, not about its props.
  */
  useEffect(() => {
    if (!import.meta.env.DEV || !inIndexTable || !hasSecondary || approvedDeviation || warnedAboutSecondary) return;

    warnedAboutSecondary = true;
    console.error(
      '<StackedCell secondary> is not allowed in a standard index table — give the second ' +
        'value its own column. See CLAUDE.md, "Table column conventions".',
    );
  }, [inIndexTable, hasSecondary, approvedDeviation]);

  return (
    <span className="min-w-0">
      {/* Wrapped only when clipped — see <TextCell> for why every cell must not be. */}
      <Tooltip label={primaryOverflow.title || undefined} block disabled={!primaryOverflow.title}>
        <span
          className={`block ${primaryOverflow.className} text-sm ${
            anchor
              ? 'font-medium text-blue-500'
              : 'text-slate-600'
          } ${primaryClassName}`}
        >
          {primary}
        </span>
      </Tooltip>

      {hasSecondary ? (
        <Tooltip
          label={secondaryOverflow.title || undefined}
          block
          disabled={!secondaryOverflow.title}
        >
        <span
          className={
            secondaryClassName ??
            `block ${secondaryOverflow.className} text-xs text-slate-400 ${mono ? 'font-mono tabular-nums' : ''}`
          }
        >
          {secondary}
        </span>
        </Tooltip>
      ) : null}
    </span>
  );
}
